import {
    DxTargetGroup,
    LegacyDxFormula,
    DxFormula,
    DxFormulaMeasure,
    DxFormulaFormat,
    DxFormatConfig,
    DxMeasureConfig,
    DxFormulaSelectionConfig,
    DxFormulaTypeId,
    DxTargetMeasureName,
} from '@/types/analytics/data-explorer.types';
import { getTargetGroupLabel } from '@/data-explorer/helpers/common.util';

export const MAX_FORMULAS = 6;

export const formulaMeasureConfigs: Record<DxFormulaMeasure, DxMeasureConfig> = {
    total: {
        id: 'total',
        label: 'Total events',
        unitLabel: 'occurrences',
    },
    visitor: {
        id: 'visitor',
        label: 'Number of unique visitors',
        unitLabel: 'unique visitors',
    },
    accounts: {
        id: 'accounts',
        label: 'Number of unique accounts',
        unitLabel: 'unique accounts',
    },
};

export const eventFormulaMeasureMapping: Record<DxTargetMeasureName, DxMeasureConfig> = {
    total: formulaMeasureConfigs.total,
    rageClicks: formulaMeasureConfigs.total,
    deadClicks: formulaMeasureConfigs.total,
    uTurns: formulaMeasureConfigs.total,
    errorClicks: formulaMeasureConfigs.total,
    numMinutes: formulaMeasureConfigs.total,
    averageMinutesPerVisitor: formulaMeasureConfigs.total,
    averageMinutesPerAccount: formulaMeasureConfigs.total,
    median: formulaMeasureConfigs.total,
    average: formulaMeasureConfigs.total,
    averageAccounts: formulaMeasureConfigs.total,
    numVisitors: formulaMeasureConfigs.visitor,
    numAccounts: formulaMeasureConfigs.accounts,
    percentVisitors: formulaMeasureConfigs.visitor,
    percentAccounts: formulaMeasureConfigs.accounts,
};

export const formulaFormatConfigs: Record<DxFormulaFormat, DxFormatConfig> = {
    decimal: { id: 'decimal', label: 'Decimal', icon: { type: 'hash' } },
    percent: { id: 'percent', label: 'Percent', icon: { type: 'percent' } },
};

export const formulaConfigs: DxFormulaSelectionConfig[] = [
    {
        id: 'sum',
        format: 'decimal',
        name: 'Sum',
        label: 'Sum',
        subLabel: 'SUM(X, Measure)',
        icon: 'sigma',
        description: 'Calculate the combined usage of multiple events for an aggregated or rolled-up view.',
    },
    {
        id: 'ratio',
        format: 'percent',
        name: 'Ratio',
        label: 'Ratio (percentage or decimal)',
        subLabel: 'SUM(X, Measure)/SUM(X, Measure)',
        icon: 'divide',
        description: 'Compare event usage to understand relationships.',
    },
    {
        id: 'relativePercentage',
        format: 'percent',
        name: 'Relative percentage',
        label: 'Relative percentage',
        subLabel: 'SUMEACH(X, Measure)/SUM(X, Measure)',
        icon: 'percent',
        description:
            'Assess the relative usage of events, or compare individual event usage within a larger event group.',
    },
];

export function isDivisionFormula (formula: DxFormula) {
    const { type } = formula;

    return type === 'ratio' || type === 'relativePercentage';
}

export function hasInvalidFormula (formulas: DxFormula[]) {
    if (!formulas || formulas.length === 0) return false;

    return formulas.some((formula) => formula.invalid);
}

/**
 * Creates a string representation of the given formula.
 *  i.e. SUMEACH (X, Measure) / SUM (X, Measure)
 *
 * @param formula The formula object to stringify
 * @param targetGroups The target groups to fetch formula measures from
 * @returns An Excel-style string representing the formula
 */
export function buildFormulaEquationString (formula: DxFormula, targetGroups: DxTargetGroup[]): string | null {
    const { type } = formula;

    if (formula.type === 'ratio' || formula.type === 'relativePercentage') {
        const numeratorTerm = getEquationString(formula.numeratorGroupId, type, targetGroups, true);
        const denominatorTerm = getEquationString(formula.denominatorGroupId, type, targetGroups);

        return `${numeratorTerm} / ${denominatorTerm}`;
    }

    if (formula.type === 'sum') {
        return getEquationString(formula.groupId, type, targetGroups);
    }

    return null;
}

export function getEquationString (
    groupId: string,
    type: DxFormulaTypeId,
    targetGroups: DxTargetGroup[],
    numerator?: boolean,
): string {
    const funcName = numerator && type === 'relativePercentage' ? 'SUMEACH' : 'SUM';
    const groupNameMap = targetGroups.reduce((acc, group, index) => {
        acc[group.id.value] = getTargetGroupLabel({
            targetGroup: group,
            targetGroupIndex: index,
            longOrShortForm: 'short',
            measure: undefined,
        });

        return acc;
    }, {} as Record<string, string>);
    const targetMeasureName = targetGroups.find(({ id }) => id.value === groupId)?.measure.value.name ?? 'total';
    const measureLabel = eventFormulaMeasureMapping[targetMeasureName].label;
    const groupName = groupNameMap[groupId];

    return `${funcName}( ${groupName}, ${measureLabel} )`;
}

/**
 * Creates a formula source type label
 * i.e. Unique Visitors / Unique Accounts
 *
 * @param formula The formula object to stringify
 * @param targetGroups The target groups to fetch formula measures from
 */
export function getFormulaSourceTypeLabel (formula: DxFormula, targetGroups: DxTargetGroup[]): string {
    const measures = getFormulaMeasures(formula, targetGroups);
    const labelTerms = [formulaMeasureConfigs[measures[0]].unitLabel];
    if (isDivisionFormula(formula)) {
        labelTerms.push('/');
        labelTerms.push(formulaMeasureConfigs[measures[1]].unitLabel);
        if (labelTerms[0] === labelTerms[2]) {
            return '';
        }
    }

    return labelTerms.join(' ');
}

export function getFormulaMeasureEquationLabel (formula: DxFormula, targetGroups: DxTargetGroup[]): string {
    const measures = getFormulaMeasures(formula, targetGroups);
    const labelTerms = [formulaMeasureConfigs[measures[0]].label];
    if (isDivisionFormula(formula)) {
        labelTerms.push('/');
        labelTerms.push(formulaMeasureConfigs[measures[1]].label);
    }

    return labelTerms.join(' ');
}

export function getFormulaGroupIds (formula: DxFormula) {
    if (formula.type === 'ratio' || formula.type === 'relativePercentage') {
        return [formula.numeratorGroupId, formula.denominatorGroupId];
    }

    return [formula.groupId];
}

export function getFormulaMeasures (formula: DxFormula, targetGroups: DxTargetGroup[]): DxFormulaMeasure[] {
    const formulaGroupIds = getFormulaGroupIds(formula);
    const formulaMeasures: Array<DxFormulaMeasure> = [];
    formulaGroupIds.forEach((groupId) => {
        const groupIdMeasure = getGroupIdMeasure(groupId, targetGroups);
        if (groupIdMeasure) {
            formulaMeasures.push(groupIdMeasure);
        }
    });

    return formulaMeasures;
}

export function getGroupIdMeasure (groupId: string, targetGroups: DxTargetGroup[]): DxFormulaMeasure | null {
    const targetGroup = targetGroups.find(({ id }) => id.value === groupId);
    if (!targetGroup) return null;

    const targetMeasure = targetGroup.measure.value.name;

    return eventFormulaMeasureMapping[targetMeasure].id;
}

export function migrateLegacyFormulas (
    formulas: Array<LegacyDxFormula | DxFormula>,
    targetGroups: DxTargetGroup[],
): DxFormula[] {
    const migratedFormulas: Array<DxFormula> = [];
    formulas?.forEach((formula) => {
        if (!('terms' in formula) && !('expression' in formula) && 'type' in formula) {
            migratedFormulas.push(formula);
        } else if ('expression' in formula && 'terms' in formula) {
            const typeId = getLegacyFormulaTypeId(formula);

            if (typeId === 'sum') {
                const newSumFormula = {
                    id: formula.id,
                    type: typeId,
                    groupId: formula.terms[0].source,
                    name: formula.name,
                    invalid: isLegacyFormulaInvalid(formula, targetGroups),
                };
                migratedFormulas.push(newSumFormula);
            } else if (typeId === 'ratio' || typeId === 'relativePercentage') {
                const newFormula: DxFormula = {
                    id: formula.id,
                    type: typeId,
                    numeratorGroupId: formula.terms[0].source,
                    denominatorGroupId: formula.terms[1].source,
                    name: formula.name,
                    format: formula.format || 'percent',
                    invalid: isLegacyFormulaInvalid(formula, targetGroups),
                };

                migratedFormulas.push(newFormula);
            }
        }
    });

    return migratedFormulas;
}

export function containsLegacyFormulas (formulas: DxFormula[] | LegacyDxFormula[]) {
    if (!formulas) return false;

    return formulas.some((formula) => {
        return 'terms' in formula && 'expression' in formula;
    });
}

export function isLegacyFormulaInvalid (formula: LegacyDxFormula, targetGroups: DxTargetGroup[]) {
    const measures: Array<{ id: string; measure: DxTargetMeasureName }> = [];
    targetGroups.forEach((targetGroup) => {
        if (targetGroup?.id?.value && targetGroup.measure?.value?.name) {
            measures.push({
                id: targetGroup.id.value,
                measure: targetGroup.measure.value.name,
            });
        }
    });

    const measureMap = new Map<string, string>();
    measures.forEach((item) => {
        measureMap.set(item.id, eventFormulaMeasureMapping[item.measure].id);
    });

    const formulaMeasuresMatchesTargetMeasures = formula.terms.every(
        (term) => term.source && term.measure && measureMap.get(term.source) === term.measure,
    );
    const isRatio = getLegacyFormulaTypeId(formula) === 'ratio';
    const isDivisionFormula = isRatio || getLegacyFormulaTypeId(formula) === 'relativePercentage';

    const isInvalidRatio = isRatio && formula.terms[0].source === formula.terms[1].source;
    const isAccountsOverVisitors =
        isDivisionFormula && formula.terms[0].measure === 'accounts' && formula.terms[1].measure === 'visitor';

    if (!formulaMeasuresMatchesTargetMeasures || isInvalidRatio) {
        return 'legacy';
    }
    if (isAccountsOverVisitors) {
        return 'accountsOverVisitors';
    }

    return undefined;
}

export function getLegacyFormulaTypeId (formula: LegacyDxFormula): DxFormulaTypeId {
    if (formula.terms.length === 1) return 'sum';

    if (formula.terms[0].function === 'sum') {
        return 'ratio';
    }

    return 'relativePercentage';
}
